import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { getRmacList, getRmacAreas, assessRmac } from '../../api/client';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// TILE_LAYERS constants for map backgrounds
const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

/** Simple centroid of a [[lat,lon],...] ring. */
function polyCentroid(latlngs) {
    if (!latlngs || latlngs.length === 0) return null;
    let lat = 0, lon = 0;
    for (const [la, lo] of latlngs) { lat += la; lon += lo; }
    return [lat / latlngs.length, lon / latlngs.length];
}

/** Great-circle distance in metres between two [lat,lon] points. */
function distM(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function collectPolygons(node) {
    const results = [];
    (node.features || []).forEach(f => {
        if (f.geom === 'Polygon' && f.coords && f.coords.length > 0) {
            // coords[0] = outer ring; coords[1..] = inner rings (holes)
            // Pass ALL rings so Leaflet can render donuts correctly.
            results.push({ name: f.name || node.name, rings: f.coords });
        }
    });
    for (const child of (node.children || [])) {
        results.push(...collectPolygons(child));
    }
    return results;
}

function RmacTreeItem({ node, aerodrome, visibleSectors, onToggle }) {
    const sectors = collectPolygons(node); // each has .name and .rings[]
    
    return (
        <div style={{ padding: '4px 0', background: 'rgba(0,0,0,0.1)' }}>
            {sectors.map((s, i) => {
                const sKey = `${aerodrome}_${s.name}`;
                const isOn = visibleSectors[sKey] !== false;
                return (
                    <div 
                        key={i}
                        onClick={() => onToggle(sKey)}
                        style={{ 
                            padding: '6px 14px 6px 36px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 10,
                            cursor: 'pointer',
                            opacity: isOn ? 1 : 0.5,
                            transition: 'opacity 0.2s'
                        }}
                    >
                        <input 
                            type="checkbox" 
                            checked={isOn} 
                            onChange={() => {}} // handled by onClick
                            style={{ accentColor: T.blue, cursor: 'pointer', transform: 'scale(0.8)' }}
                        />
                        <span style={{ fontSize: 11, color: isOn ? T.blueLt : T.muted }}>
                            -- {s.name || `Sector ${i+1}`}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export default function RmacPage({ project, arps, obstacles, loading, rmacState, setRmacState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const { getStyle } = useSurfaceStyles();
    const rangeRingStyle = getStyle('rmac.range_ring');
    const sectorStyle = getStyle('rmac.sector');
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate an RMAC sector, not just its tallest point overall.
    const rmacResultsForShield = rmacState?.rmacResults || {};
    const penetratingObstacleIds = useMemo(() => {
        const ids = [];
        Object.values(rmacResultsForShield).forEach(entry => {
            (entry?.data?.results || []).forEach(r => { if (r.is_penetration) ids.push(r.obstacle_id); });
        });
        return ids;
    }, [rmacResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    // ── RMAC shielding — real per-sector check, not just flat elevation ────────
    // RMAC sectors around the reference VOR aren't sloped, but they ARE
    // zoned with different minima per sector, so a candidate in a stricter
    // sector at a modest elevation can genuinely penetrate while the
    // project's own (taller) obstacle sits in a more lenient sector — the
    // same gap flat elevation comparison had for IHS (see OLSPage.jsx).
    // result_diff (MOCA ft - sector minima ft) is already positive-when-
    // penetrating, per evaluate_rmac in rmac.py.
    const rmacImpacts = useMemo(() => {
        const out = {}; // `${icao}_${sector}` -> worst (highest) result_diff
        Object.entries(rmacResultsForShield).forEach(([icao, entry]) => {
            (entry?.data?.results || []).forEach(r => {
                if (!r.is_penetration || r.result_diff == null) return;
                const key = `${icao}_${r.sector_name}`;
                if (!(key in out) || r.result_diff > out[key]) out[key] = r.result_diff;
            });
        });
        return out;
    }, [rmacResultsForShield]);
    const hasRmacImpact = Object.keys(rmacImpacts).length > 0;

    const [rmacShieldPens, setRmacShieldPens] = useState({}); // icao -> candidate result rows
    useEffect(() => {
        if (!shieldOn || !shieldData || !hasRmacImpact) { setRmacShieldPens({}); return; }
        const icaos = [...new Set(Object.keys(rmacImpacts).map(k => k.split('_')[0]))];
        if (!icaos.length) return;
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setRmacShieldPens({}); return; }
        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));
        let cancelled = false;
        (async () => {
            const perIcao = {};
            for (const icao of icaos) {
                try {
                    const { data } = await assessRmac(icao, pseudoObstacles);
                    perIcao[icao] = data.results || [];
                } catch (e) {
                    console.error(`RMAC shield check failed for ${icao}`, e);
                }
            }
            if (!cancelled) setRmacShieldPens(perIcao);
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasRmacImpact, rmacImpacts]);

    // Candidate pseudo-id -> [{ icao, sector, diff, projectDiff }, ...] —
    // beats the project's own worst on that sector (the amber "true shield").
    const rmacShieldVerdicts = useMemo(() => {
        const out = new Map();
        Object.entries(rmacShieldPens).forEach(([icao, rows]) => {
            rows.forEach(r => {
                if (!r.is_penetration || r.result_diff == null) return;
                const key = `${icao}_${r.sector_name}`;
                const projectDiff = rmacImpacts[key];
                if (projectDiff == null) return;
                if (r.result_diff >= projectDiff) {
                    const list = out.get(r.obstacle_id) || [];
                    list.push({ icao, sector: r.sector_name, diff: r.result_diff, projectDiff });
                    out.set(r.obstacle_id, list);
                }
            });
        });
        return out;
    }, [rmacShieldPens, rmacImpacts]);

    const rmacShieldInsideSurface = useMemo(() => {
        const out = new Map();
        Object.values(rmacShieldPens).forEach(rows => {
            rows.forEach(r => { if (r.sector_name) out.set(r.obstacle_id, true); });
        });
        return out;
    }, [rmacShieldPens]);

    // Candidate pseudo-id -> [{ sector, margin }, ...] for every sector it
    // genuinely penetrates at its own position, regardless of raw elevation
    // or of beating the project's worst — see olsShieldRealPenetration in
    // OLSPage.jsx.
    const rmacShieldRealPenetration = useMemo(() => {
        const out = new Map();
        Object.values(rmacShieldPens).forEach(rows => {
            rows.forEach(r => {
                if (!r.is_penetration || r.result_diff == null) return;
                const list = out.get(r.obstacle_id) || [];
                list.push({ sector: r.sector_name, margin: r.result_diff });
                out.set(r.obstacle_id, list);
            });
        });
        return out;
    }, [rmacShieldPens]);

    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real sector
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasRmacImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = rmacShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = rmacShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = rmacShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v => `RMAC ${v.sector} (+${v.margin.toFixed(0)} ft)`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">RMAC ${v.sector}</b> — this obstacle exceeds minima by ${v.diff.toFixed(0)} ft (project worst: ${v.projectDiff.toFixed(0)} ft)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">RMAC: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, rmacShieldVerdicts, rmacShieldInsideSurface, rmacShieldRealPenetration, hasRmacImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield an
    // RMAC sector — same pattern as OLSPage.jsx.
    const rmacTrueShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (rmacTrueShieldLayerRef.current) { mapInst.current.removeLayer(rmacTrueShieldLayerRef.current); rmacTrueShieldLayerRef.current = null; }
        if (!shieldOn || rmacShieldVerdicts.size === 0) return;
        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });
        rmacShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>RMAC ${v.sector}</b> (${v.icao}) — exceeds minima by <b>${v.diff.toFixed(0)} ft</b> (project worst: ${v.projectDiff.toFixed(0)} ft)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>📡 TRUE SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact ${verdicts.length > 1 ? 'on these sectors' : 'on this sector'}.</span>`
            );
            group.addLayer(marker);
        });
        if (group.getLayers().length > 0) {
            rmacTrueShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, rmacShieldVerdicts, shieldData]);
    const tileLyr = useRef(null);
    const mkrLyrs = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [selObstId, setSelId] = useState('');
    const [mapState, setMapState] = useState(null);
    const [assessing, setAssessing] = useState({});  // { [icao]: bool }
    const lastFittedId = useRef(null);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(400, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    const { visible, rmacList, rmacLayers, rmacResults, visibleSectors } = rmacState;

    const obsWithCoords = obstacles.filter(o => o.latitude && o.longitude);
    const selectedObs = obsWithCoords.find(o => o.id === selObstId) || obsWithCoords[0];

    const updateState = useCallback((delta) => setRmacState(prev => ({ ...prev, ...delta })), [setRmacState]);

    const getDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    // ── Fetch designated RMAC aerodromes from backend
    useEffect(() => {
        const fetchList = async () => {
            let list = rmacList;
            if (list.length === 0) {
                const res = await getRmacList();
                list = res.data || [];
            }

            const vis = { ...visible };
            let changed = list.length !== rmacList.length;

            list.forEach(r => {
                // Always recalculate from arps/obstacles (never short-circuit on prior state)
                const arp = arps.find(x => x.icao === r.aerodrome);
                let isNearby = false;
                // Backend returns arp.lat / arp.lon (NOT arp.latitude / arp.longitude)
                if (arp && arp.lat != null && arp.lon != null) {
                    isNearby = obsWithCoords.some(o => {
                        const dist = getDistance(arp.lat, arp.lon, o.latitude, o.longitude);
                        return dist <= 101.86; // 55 NM in km
                    });
                }
                if (vis[r.aerodrome] !== isNearby) {
                    vis[r.aerodrome] = isNearby;
                    changed = true;
                }
            });

            if (changed) {
                updateState({ rmacList: list, visible: vis });
            }
        };
        fetchList().catch(err => console.error("Failed to fetch RMAC list:", err));
    }, [arps, obsWithCoords]); // Re-check whenever arps or obstacles change

    // ── Map init
    useEffect(() => {
        if (mapInst.current || !mapRef.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6 });
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { ro.disconnect(); mapInst.current?.remove(); mapInst.current = null; onMapReady?.(null); };
    }, []);

    // ── Toggle base layers
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileLyr.current) mapInst.current.removeLayer(tileLyr.current);
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // ── Fetch JSON area polygons for visible RMACs
    useEffect(() => {
        Object.entries(visible).forEach(([icao, isVis]) => {
            if (isVis && !rmacLayers[icao]) {
                // Use a local "fetching" flag if needed to prevent double fetch,
                // but checking rmacLayers[icao] is usually enough if update is fast.
                getRmacAreas(icao).then(res => {
                    const layers = res.data?.json_layers || res.data;
                    setRmacState(prev => ({
                        ...prev,
                        rmacLayers: { ...prev.rmacLayers, [icao]: layers }
                    }));
                }).catch(err => console.error(`Failed to fetch JSON areas for ${icao}`, err));
            }
        });
    }, [visible]); // Only depend on visible; rmacLayers handled by functional update

    // ── Render features
    useEffect(() => {
        if (!mapInst.current || loading) return;
        const map = mapInst.current;
        mkrLyrs.current.forEach(layer => map.removeLayer(layer));
        mkrLyrs.current = [];

        const bounds = [];
        const drawnPolys = [];

        // Draw sector polygons for visible aerodromes
        rmacList.forEach(rmacInfo => {
            const icao = rmacInfo.aerodrome;
            if (visible[icao] === false) return;
            const layers = rmacLayers[icao];
            if (!layers) return;

            // Find ARP and draw 55NM (101.86km) range ring
            const arp = arps.find(x => x.icao === icao);
            if (arp && arp.lat != null && arp.lon != null) {
                const m = L.marker([arp.lat, arp.lon], { icon: getArpIcon(L, arp, showArpLabels) })
                    .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}`);
                if (onAerodromeContextMenu) {
                    m.on('contextmenu', e => {
                        e.originalEvent.preventDefault();
                        onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                    });
                }
                m.addTo(map);
                mkrLyrs.current.push(m);

                const circle = L.circle([arp.lat, arp.lon], {
                    radius: 101860, // 55 NM in meters
                    color: rangeRingStyle.color, weight: 1, dashArray: '5, 10', fill: false
                }).bindTooltip(`55 NM Range (${icao})`, { sticky: true });
                circle.addTo(map);
                mkrLyrs.current.push(circle);
                drawnPolys.push([arp.lat, arp.lon]);
            }

            const polys = collectPolygons(layers);
            polys.forEach(p => {
                const sKey = `${icao}_${p.name}`;
                if (visibleSectors && visibleSectors[sKey] === false) return;

                // rings[0] = outer ring; rings[1..] = inner rings (holes)
                const rings = p.rings;
                const outerLatlngs = rings[0];

                // Only render if centroid is within 55 NM of ARP
                const centroid = polyCentroid(outerLatlngs);
                if (centroid && arp && arp.lat != null && arp.lon != null) {
                    const d = distM(centroid[0], centroid[1], arp.lat, arp.lon);
                    if (d > 101860) return; // outside 55 NM — skip
                }

                // Pass all rings to L.polygon — Leaflet punches holes for rings[1+]
                const poly = L.polygon(rings, {
                    color: sectorStyle.color,
                    weight: 1.5,
                    fillColor: sectorStyle.color,
                    fillOpacity: sectorStyle.opacity,
                    dashArray: '4, 4'
                }).bindTooltip(`<b>${icao} RMAC</b><br/>${p.name}`, { sticky: true });
                poly.addTo(map);
                mkrLyrs.current.push(poly);
                outerLatlngs.forEach(ll => drawnPolys.push(ll));
            });
        });

        // Draw All Obstacles
        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude]; bounds.push(ll);
            const isSel = o.id === selectedObs?.id;
            let isPenetrating = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';
            
            rmacList.forEach(rmacInfo => {
                const icao = rmacInfo.aerodrome;
                if (!visible[icao]) return;
                const resData = rmacResults[icao]?.data?.results;
                const obsRes = resData?.find(r => r.obstacle_id === o.id);
                if (obsRes) {
                    foundAny = true;
                    if (obsRes.is_penetration) isPenetrating = true;
                    if (obsRes.result_diff != null) {
                        const pen = obsRes.result_diff * 0.3048;
                        if (pen > maxPen) {
                            maxPen = pen;
                            surfaceStr = `RMAC Sector ${obsRes.sector_name || '—'} (${icao})`;
                        }
                    }
                }
            });

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const obsIconUrl = isPenetrating ? '/symbols/obstacle_red.svg' : '/symbols/obstacle_green.svg';
            const sz = isSel ? 80 : 65; const h = Math.round(sz / 2);
            const icon = getObstacleIcon(L, o, isPenetrating ? "penetrated" : "clear", showObsLabels);
            const m = L.marker(ll, { icon, zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(map);
            mkrLyrs.current.push(m);
        });
        addHullPolygonsToMap(map, obsWithCoords, project?.id, mkrLyrs);

        // Autofit and setView logic removed per user request to stop unwanted zooming on checkbox toggles.
    }, [rmacList, rmacLayers, selectedObs, visible, loading, rmacResults, visibleSectors, showObstacles, showObsLabels, showArpLabels, onObstacleContextMenu, onAerodromeContextMenu, rangeRingStyle, sectorStyle]);

    // ── Manual RMAC assessment trigger (like NPA's runAssessment)
    const runRmacAssessment = async (icao) => {
        if (!obsWithCoords.length) return;
        setAssessing(prev => ({ ...prev, [icao]: true }));
        // Ensure areas are loaded before assessing
        if (!rmacLayers[icao]) {
            try {
                const res = await getRmacAreas(icao);
                const layers = res.data?.json_layers || res.data;
                setRmacState(prev => ({ ...prev, rmacLayers: { ...prev.rmacLayers, [icao]: layers } }));
            } catch (err) {
                console.error(`Failed to fetch RMAC areas for ${icao}`, err);
            }
        }
        setRmacState(prev => ({
            ...prev,
            rmacResults: { ...prev.rmacResults, [icao]: { loading: true } }
        }));
        try {
            const res = await assessRmac(icao, obsWithCoords);
            const resultData = res.data || {};
            setRmacState(prev => ({
                ...prev,
                rmacResults: {
                    ...prev.rmacResults,
                    [icao]: { loading: false, data: resultData, error: resultData.error }
                }
            }));
        } catch (err) {
            setRmacState(prev => ({
                ...prev,
                rmacResults: { ...prev.rmacResults, [icao]: { loading: false, error: err.response?.data?.detail || err.message } }
            }));
        } finally {
            setAssessing(prev => ({ ...prev, [icao]: false }));
        }
    };

    const renderResultsContent = () => {
        if (rmacList.length === 0 || Object.keys(rmacResults).length === 0) {
            return (
                <div style={{ padding: 30, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                    Run assessment to view the RMAC report.
                </div>
            );
        }
        return rmacList.map(({ aerodrome: icao }) => {
            const res = rmacResults[icao];
            if (!res) return null; // not yet assessed

            // Mark the controlling obstacle (highest result_diff = worst clearance) per sector
            if (res.data?.results?.length) {
                const worstBySector = new Map();
                res.data.results.forEach(r => {
                    if (r.result_diff == null || !r.sector_name) return;
                    const cur = worstBySector.get(r.sector_name);
                    if (!cur || r.result_diff > cur.result_diff) worstBySector.set(r.sector_name, r);
                });
                worstBySector.forEach(r => { r.isControlling = true; });
            }

            return (
                <div key={icao} style={{ padding: 20, borderBottom: `1px dashed ${T.border}`, color: T.white }}>
                    {res?.loading ? (
                        <div style={{ color: T.muted }}>Assessing {icao}...</div>
                    ) : res?.error ? (
                        <div style={{ color: '#e84040' }}>Error evaluating {icao}: {typeof res.error === 'string' ? res.error : JSON.stringify(res.error)}</div>
                    ) : res?.data?.results ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div style={{ fontSize: 16, fontWeight: 'bold', color: T.blueLt }}>{icao} Sector-Based RMAC Assessment</div>
                                <button
                                    onClick={() => {
                                        const headers = ["Sector", "#", "Obstacle Name", "Elevation (m)", "Vertical Accuracy (m)", "Total Elevation (m)", "MOCA (ft)", "Sector Minima (ft)", "Clearance (ft)", "Bearing (deg T)", "Distance (NM)", "Result", "Controlling"];
                                        const bySector = new Map();
                                        const noSector = [];
                                        res.data.results.forEach(r => {
                                            if (!r.sector_name) { noSector.push(r); return; }
                                            if (!bySector.has(r.sector_name)) bySector.set(r.sector_name, []);
                                            bySector.get(r.sector_name).push(r);
                                        });
                                        const sectorNum = name => parseInt((name || '').replace(/\D/g, ''), 10) || 0;
                                        const sectorNames = [...bySector.keys()].sort((a, b) => sectorNum(a) - sectorNum(b));
                                        const rows = [];
                                        const pushGroup = (label, groupRows) => groupRows.forEach((r, idx) => rows.push([
                                            label, idx + 1, r.obstacle_name, r.elevation_m, r.v_tolerance, r.total_elevation_m,
                                            r.moca_ft, r.sector_minima_ft || '-',
                                            r.result_diff != null ? Math.abs(r.result_diff).toFixed(0) : '-',
                                            r.bearing_deg ?? '-', r.distance_nm ?? '-',
                                            r.is_penetration ? 'PENETRATES' : 'CLEAR', r.isControlling ? 'Yes' : ''
                                        ]));
                                        sectorNames.forEach(name => pushGroup(name, bySector.get(name)));
                                        if (noSector.length > 0) pushGroup('Outside All Sectors', noSector);
                                        exportToCsv(headers, rows, `${icao}_RMAC_Sector_Assessment.csv`);
                                    }}
                                    style={{
                                        padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                        background: T.blue, color: T.white, fontSize: 11, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: 4
                                    }}
                                >
                                    📥 Export CSV
                                </button>
                            </div>
                            
                            {/* Obstacle Results Table — grouped by sector, like the Circling CAT A-D layout */}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ background: T.bg0, borderBottom: `1px solid ${T.border}` }}>
                                            <th style={{ padding: '10px 6px', color: T.muted, width: 24 }}>#</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Obstacle Name</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Elevation (m)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Vertical Accuracy (m)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Total Elevation (m)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>MOCA (ft)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Sector Minima (ft)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Clearance (ft)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Bearing °T</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Distance (NM)</th>
                                            <th style={{ padding: '10px 6px', color: T.muted, whiteSpace: 'nowrap' }}>Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const bySector = new Map();
                                            const noSector = [];
                                            res.data.results.forEach(r => {
                                                if (!r.sector_name) { noSector.push(r); return; }
                                                if (!bySector.has(r.sector_name)) bySector.set(r.sector_name, []);
                                                bySector.get(r.sector_name).push(r);
                                            });
                                            const sectorNum = name => parseInt((name || '').replace(/\D/g, ''), 10) || 0;
                                            const sectorNames = [...bySector.keys()].sort((a, b) => sectorNum(a) - sectorNum(b));

                                            const renderGroup = (label, rows, key) => (
                                                <React.Fragment key={key}>
                                                    <tr style={{ background: 'rgba(58,138,196,0.12)' }}>
                                                        <td colSpan={11} style={{ padding: '8px 6px', fontWeight: 800, color: T.blueLt }}>{label.toUpperCase()}</td>
                                                    </tr>
                                                    {rows.length === 0 ? (
                                                        <tr><td colSpan={11} style={{ padding: '8px 6px', color: T.muted, fontStyle: 'italic' }}>No obstacles in {label}</td></tr>
                                                    ) : rows.map((r, idx) => (
                                                        <tr key={idx} style={{ borderBottom: `1px solid ${T.border}33`, background: r.is_penetration ? 'rgba(232,64,64,0.08)' : 'transparent' }}>
                                                            <td style={{ padding: '8px 6px', color: T.muted }}>{idx + 1}</td>
                                                            <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                                                                {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                            </td>
                                                            <td style={{ padding: '8px 6px' }}>{r.elevation_m}</td>
                                                            <td style={{ padding: '8px 6px', color: T.mutedLt }}>{r.v_tolerance}</td>
                                                            <td style={{ padding: '8px 6px' }}>{r.total_elevation_m}</td>
                                                            <td style={{ padding: '8px 6px', color: T.blueLt, fontWeight: 700 }}>{r.moca_ft}</td>
                                                            <td style={{ padding: '8px 6px' }}>{r.sector_minima_ft || '—'}</td>
                                                            <td style={{ padding: '8px 6px' }}>
                                                                {r.result_diff != null ? (
                                                                    <span style={{ color: r.is_penetration ? '#e84040' : T.white, fontWeight: r.is_penetration ? 'bold' : 'normal' }}>
                                                                        {Math.abs(r.result_diff).toFixed(0)}
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td style={{ padding: '8px 6px' }}>{r.bearing_deg ?? '—'}°</td>
                                                            <td style={{ padding: '8px 6px' }}>{r.distance_nm ?? '—'}</td>
                                                            <td style={{ padding: '8px 6px' }}>
                                                                <span style={{
                                                                    display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                                                                    color: '#fff',
                                                                    background: r.is_penetration ? '#e8404033' : '#2ec98b33',
                                                                    border: `1px solid ${r.is_penetration ? '#e8404055' : '#2ec98b55'}`
                                                                }}>
                                                                    {r.is_penetration ? 'PENETRATES' : '✓ CLEAR'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            );

                                            if (sectorNames.length === 0 && noSector.length === 0) {
                                                return <tr><td colSpan={11} style={{ padding: 16, textAlign: 'center', color: T.muted }}>No obstacles found within RMAC sectors.</td></tr>;
                                            }
                                            return (
                                                <>
                                                    {sectorNames.map(name => renderGroup(name, bySector.get(name), name))}
                                                    {noSector.length > 0 && renderGroup('Outside All Sectors', noSector, '__no_sector')}
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ marginTop: 24 }}>
                                <div style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.2)', fontSize: 13, fontWeight: 700, color: T.blueLt }}>RMAC REFERENCE CHART</div>
                                <div style={{ display: 'flex', justifyContent: 'center', background: 'rgba(0,0,0,0.1)', borderRadius: 4, minHeight: 180, alignItems: 'center', marginTop: 12 }}>
                                    <img 
                                        src={`${API_BASE}/rmac/image/${icao}`} 
                                        alt={`${icao} RMAC`} 
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                        style={{ maxWidth: '100%', borderRadius: 4, display: 'block' }} 
                                    />
                                    <div style={{ display: 'none', color: T.muted, fontSize: 12 }}>Reference chart image not available for {icao}</div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div key={icao} style={{ padding: 20, color: T.muted, textAlign: 'center', border: `1px dashed ${T.border}`, borderRadius: 6 }}>
                            {res?.loading ? `Assessing ${icao}...` : (res?.error ? `Error: ${res.error}` : `No obstacles found within RMAC sectors for ${icao}.`)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                {/* ── Left Sidebar (Checklist + Target) ── */}
                <div style={{ width: 280, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Target Obstacle
                        </div>
                        <select
                            value={selObstId || ''}
                            onChange={(e) => setSelId(e.target.value)}
                            style={{
                                width: '100%', background: T.bg0, border: `1px solid ${T.border}`,
                                color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13, outline: 'none'
                            }}
                        >
                            {obsWithCoords.map(o => (
                                <option key={o.id} value={o.id}>{o.name || 'Unnamed'}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {/* Only list aerodromes that are within the 55 NM screening area */}
                        {(() => {
                            const inRangeList = rmacList.filter(a => visible[a.aerodrome] === true);
                            const loadingList = rmacList.length > 0 && Object.keys(visible).length === 0;
                            return (<>
                        <div style={{ padding: '12px 16px', fontSize: 11, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', background: `rgba(255,255,255,0.02)` }}>
                            RMAC Aerodromes ({inRangeList.length})
                        </div>
                        {!loadingList && inRangeList.length === 0 && (
                            <div style={{ padding: 16, color: T.muted, fontSize: 12, fontStyle: 'italic' }}>No RMAC aerodromes within 55 NM of project obstacles.</div>
                        )}
                        {inRangeList.map((a, i) => {
                            const icao = a.aerodrome;
                            const isOn = visible[icao] !== false;
                            const layers = rmacLayers[icao];
                            const isAssessing = !!assessing[icao];
                            const res = rmacResults[icao];
                            const isAssessed = !!(res && !res.loading && (res.data || res.error));

                            return (
                                <div key={i} style={{ borderBottom: `1px solid ${T.border}33` }}>
                                    {/* Aerodrome row — name + Assess button, like NPA */}
                                    <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {/* Visibility checkbox */}
                                        <input
                                            type="checkbox"
                                            checked={isOn}
                                            onChange={() => {
                                                const newIsOn = !isOn;
                                                const newVisible = { ...visible, [icao]: newIsOn };
                                                const newSectors = { ...(visibleSectors || {}) };
                                                if (layers) {
                                                    collectPolygons(layers).forEach(s => {
                                                        newSectors[`${icao}_${s.name}`] = newIsOn; // s.name unchanged
                                                    });
                                                }
                                                setRmacState(prev => ({ ...prev, visible: newVisible, visibleSectors: newSectors }));
                                            }}
                                            style={{ cursor: 'pointer', accentColor: T.blue, flexShrink: 0 }}
                                            title="Toggle RMAC sectors on map"
                                        />
                                        {/* Name + subtitle */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: isAssessed ? T.white : T.blue, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {icao}
                                            </div>
                                            <div style={{ fontSize: 10, color: T.blueLt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {a.chart_name || 'RMAC Area'}
                                            </div>
                                        </div>
                                        {/* Assess button */}
                                        <button
                                            onClick={() => runRmacAssessment(icao)}
                                            disabled={isAssessing}
                                            style={{
                                                padding: '3px 10px', fontSize: 10, fontWeight: 700,
                                                background: isAssessed ? 'rgba(46,201,139,0.15)' : 'rgba(58,138,196,0.2)',
                                                border: `1px solid ${isAssessed ? T.safe : T.blue}44`,
                                                borderRadius: 5,
                                                color: isAssessed ? T.safe : T.blue,
                                                cursor: isAssessing ? 'wait' : 'pointer',
                                                flexShrink: 0,
                                            }}
                                            title={isAssessed ? 'Re-run RMAC assessment' : 'Load RMAC assessment'}
                                        >
                                            {isAssessing ? '⟳' : isAssessed ? '↺ Re-run' : '⬇ Load'}
                                        </button>
                                    </div>

                                    {/* Sector toggles — shown once KMZ layers are loaded */}
                                    {layers ? (
                                        <RmacTreeItem
                                            node={layers}
                                            aerodrome={icao}
                                            visibleSectors={visibleSectors || {}}
                                            onToggle={(sKey) => {
                                                const next = { ...(visibleSectors || {}) };
                                                next[sKey] = next[sKey] === false ? true : false;
                                                updateState({ visibleSectors: next });
                                            }}
                                        />
                                    ) : (
                                        isOn && <div style={{ padding: '6px 40px', fontSize: 10, color: T.mutedLt, fontStyle: 'italic' }}>Loading KMZ…</div>
                                    )}
                                </div>
                            );
                        })}
                        </>);
                        })()}
                    </div>
                </div>

                {/* ── Main content (Map + Results) ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, position: 'relative', background: '#0a192f' }}>
                        {loading && <div style={{ position: 'absolute', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.5)', color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>Loading Map…</div>}
                        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                            {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                                <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                            ))}
                        </div>
                        <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                            penetratingCount={rmacShieldRealPenetration.size}
                            onExportPenetrating={() => {
                                const byId = new Map();
                                [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                                 ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                                    byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                                });
                                const entries = [];
                                rmacShieldRealPenetration.forEach((penList, obsId) => {
                                    const r = byId.get(obsId);
                                    if (!r) return;
                                    penList.forEach(v => entries.push({
                                        r,
                                        surfaceLabel: `RMAC ${v.sector}`,
                                        penetrationAmount: v.margin.toFixed(2),
                                    }));
                                });
                                exportSurfacePenetratingCsv(entries, radiusKm);
                            }} />
                        <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 400} />
                        <MapCoordinateViewer map={mapState} />
                        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                    </div>

                    <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden' }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>📡</span> RMAC Sector Assessment Report
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out. 
                                    <button 
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - RMAC Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
